// ==========================================================================
// FIREBASE DATABASE CONNECTOR & HELPER FUNCTIONS (ESM VERSION)
// ==========================================================================

import { initializeApp } from "firebase/app";
import { 
  getFirestore, doc, collection, getDoc, getDocs, setDoc, updateDoc, deleteDoc, query, where, increment 
} from "firebase/firestore";
import { 
  getStorage, ref, uploadBytes, getDownloadURL, getBlob 
} from "firebase/storage";
import { 
  getAuth 
} from "firebase/auth";

// Firebase configuration provided by the USER
const firebaseConfig = {
  apiKey: "AIzaSyAZ4bc357MKE3yZihxkoxAl9bt1iXDdexE",
  authDomain: "wurupayroll.firebaseapp.com",
  projectId: "wurupayroll",
  storageBucket: "wurupayroll.firebasestorage.app",
  messagingSenderId: "349243937460",
  appId: "1:349243937460:web:cf48b955ba9d6c00ab3de1",
  measurementId: "G-P11MHPEVYK"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const storage = getStorage(app);
const auth = getAuth(app);

export { db, storage, auth };

// ==========================================================================
// IMAGE RESIZER & WEBP CONVERTER (CLIENT SIDE)
// ==========================================================================
export async function convertToWebP(file) {
  // If the file is a PDF, render first page and convert to WebP
  if (file.type === 'application/pdf') {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async function(e) {
        try {
          const pdfData = new Uint8Array(e.target.result);
          const pdfjsLib = window.pdfjsLib || window['pdfjs-dist/build/pdf'];
          if (!pdfjsLib) {
            reject(new Error("ไลบรารี PDF.js ไม่พร้อมใช้งาน"));
            return;
          }
          pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
          
          const loadingTask = pdfjsLib.getDocument({ data: pdfData });
          const pdf = await loadingTask.promise;
          const page = await pdf.getPage(1);
          
          const viewport = page.getViewport({ scale: 1.5 });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const ctx = canvas.getContext('2d');
          
          await page.render({ canvasContext: ctx, viewport: viewport }).promise;
          
          canvas.toBlob((blob) => {
            if (blob) {
              const webpFile = new File([blob], file.name.substring(0, file.name.lastIndexOf('.')) + '.webp', {
                type: 'image/webp',
                lastModified: Date.now()
              });
              resolve(webpFile);
            } else {
              reject(new Error("Failed to convert PDF canvas to WebP"));
            }
          }, 'image/webp', 0.85);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error("Failed to read PDF file"));
      reader.readAsArrayBuffer(file);
    });
  }

  // If the file is not an image, return it directly (e.g. PDFs)
  if (!file.type.startsWith('image/')) {
    return file;
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = function(e) {
      const img = new Image();
      img.onload = function() {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        canvas.toBlob((blob) => {
          if (blob) {
            // Create a webp file out of the blob
            const webpFile = new File([blob], file.name.substring(0, file.name.lastIndexOf('.')) + '.webp', {
              type: 'image/webp',
              lastModified: Date.now()
            });
            resolve(webpFile);
          } else {
            reject(new Error("Failed to convert image to WebP"));
          }
        }, 'image/webp', 0.85); // 85% compression quality
      };
      img.onerror = () => reject(new Error("Failed to load image in browser"));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

// ==========================================================================
// COMPANY CONFIGURATION CRUD
// ==========================================================================
export async function getCompanySettings(companyId) {
  const compRef = doc(db, 'companies', companyId);
  const snap = await getDoc(compRef);
  if (snap.exists()) {
    const data = snap.data();
    if (data.holidaySystem === undefined) {
      data.holidaySystem = companyId === 'itembox' ? 'admin_defined' : 'employee_selected';
    }
    if (data.companyHolidaysQuota === undefined) {
      data.companyHolidaysQuota = 13;
    }
    if (data.allowEmployeesToViewOthersLeave === undefined) {
      data.allowEmployeesToViewOthersLeave = true;
    }
    if (data.publicHolidaySyncDays === undefined) {
      data.publicHolidaySyncDays = 7;
    }
    return data;
  } else {
    // Generate default settings for missing company
    const defaults = {
      id: companyId,
      name: companyId === 'itembox' ? 'ITEMBOX' : 'MEANBOOK',
      address: companyId === 'itembox' 
        ? '123/45 อาคารไอเทมบ็อกซ์ ชั้น 5 ถนนรัชดาภิเษก แขวงดินแดง เขตดินแดง กรุงเทพมหานคร 10400' 
        : '88/9 อาคารสเปซกริด ชั้น 12 ถนนสุขุมวิท แขวงคลองเตย เขตคลองเตย กรุงเทพมหานคร 10110',
      taxId: companyId === 'itembox' ? '0105566000123' : '0105567000987',
      phone: companyId === 'itembox' ? '02-123-4567' : '02-987-6543',
      theme: companyId === 'itembox' ? 'peach-cream' : 'cool-grey',
      socialSecurityRate: 5,
      socialSecurityMin: 82.5,
      socialSecurityMax: 875,
      withholdingTaxRate: 3,
      authorizedPayors: ['นัททราย ดุสิตตานครินทร์'],
      enableSickLeaveCalc: true,
      enableAnnualLeaveCalc: true,
      holidaySystem: companyId === 'itembox' ? 'admin_defined' : 'employee_selected',
      companyHolidaysQuota: 13,
      allowEmployeesToViewOthersLeave: true,
      diligenceAllowanceAmount: 1000,
      diligenceRequireNoLate: true,
      diligenceRequireNoAbsence: true,
      diligenceRequireNoSickLeave: true,
      diligenceRequireNoAnnualLeave: true,
      diligenceRequireNoPersonalLeave: true,
      workHoursPerDay: {
        monday: 8, tuesday: 8, wednesday: 8, thursday: 8, friday: 8, saturday: 8, sunday: 0
      },
      publicHolidaySyncDays: 7
    };
    await setDoc(compRef, defaults);
    return defaults;
  }
}

export async function saveCompanySettings(companyId, settings) {
  const compRef = doc(db, 'companies', companyId);
  await setDoc(compRef, settings, { merge: true });
}

// ==========================================================================
// EMPLOYEE CRUD
// ==========================================================================
export async function getEmployees(companyId) {
  const q = query(collection(db, 'employees'), where('companyId', '==', companyId));
  const snap = await getDocs(q);
  const list = [];
  snap.forEach((doc) => {
    list.push({ id: doc.id, ...doc.data() });
  });
  return list;
}

export async function addEmployee(employeeData) {
  // Generate random safe ID if not provided
  const id = employeeData.id || 'emp_' + Math.random().toString(36).substr(2, 9);
  const empRef = doc(db, 'employees', id);
  const payload = { ...employeeData, id };
  await setDoc(empRef, payload);
  return id;
}

export async function updateEmployee(employeeId, employeeData) {
  const empRef = doc(db, 'employees', employeeId);
  await updateDoc(empRef, employeeData);
}

export async function deleteEmployee(employeeId) {
  const empRef = doc(db, 'employees', employeeId);
  await deleteDoc(empRef);
}

// Filter resigned employees active or archived (resigned for >= 2 years)
export async function getResignedArchiveEmployees(companyId) {
  const q = query(
    collection(db, 'employees'), 
    where('companyId', '==', companyId),
    where('status', '==', 'resigned')
  );
  const snap = await getDocs(q);
  const list = [];
  const twoYearsAgoMs = 2 * 365 * 24 * 60 * 60 * 1000;
  const cutoffTime = Date.now() - twoYearsAgoMs;

  snap.forEach((doc) => {
    const data = doc.data();
    if (data.resignedDate) {
      const resignTime = new Date(data.resignedDate).getTime();
      if (resignTime <= cutoffTime) {
        list.push({ id: doc.id, ...data });
      }
    }
  });
  return list;
}

// ==========================================================================
// TIMESHEET & RECORD ATTENDANCE (WITH IN-MEMORY FAST CACHE)
// ==========================================================================

// Short-term in-memory cache for fast tab-switching & report rendering (45s TTL)
const timesheetCache = new Map();
const TIMESHEET_CACHE_TTL = 45000;

export function clearTimesheetCache() {
  timesheetCache.clear();
}

export function invalidateTimesheet(employeeId, month) {
  const tsId = `timesheet_${employeeId}_${month}`;
  timesheetCache.delete(tsId);
}

export async function getTimesheet(employeeId, month) {
  const tsId = `timesheet_${employeeId}_${month}`;
  
  const cached = timesheetCache.get(tsId);
  if (cached && (Date.now() - cached.timestamp < TIMESHEET_CACHE_TTL)) {
    return JSON.parse(JSON.stringify(cached.data));
  }

  const tsRef = doc(db, 'timesheets', tsId);
  const snap = await getDoc(tsRef);
  let result;
  if (snap.exists()) {
    result = snap.data();
  } else {
    // Generate default blank timesheet
    const daysInMonth = new Date(parseInt(month.split('-')[0]), parseInt(month.split('-')[1]), 0).getDate();
    const days = {};
    const now = new Date();
    const todayString = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    for (let d = 1; d <= daysInMonth; d++) {
      const dateString = `${month}-${String(d).padStart(2, '0')}`;
      days[d] = {
        status: dateString > todayString ? 'not_started' : 'work', // 'work' | 'not_started' | 'absence' | 'leave_sick' | ...
        lateMinutes: 0,
        earlyLeaveMinutes: 0,
        advance: 0
      };
    }
    result = {
      id: tsId,
      employeeId,
      month,
      isLocked: false,
      isPhantom: true,
      days,
      summary: {
        basicSalaryEarned: 0,
        taxableIncome: 0,
        diligenceAllowance: 0,
        lateDeduction: 0,
        securityDepositDeducted: 0,
        studentLoanDeducted: 0,
        socialSecurityDeducted: 0,
        withholdingTaxDeducted: 0,
        otherIncomes: 0,
        otherDeductions: 0,
        totalAdvance: 0,
        netSalary: 0
      },
      transferSlipUrl: '',
      transferSlipUploadedAt: ''
    };
  }

  timesheetCache.set(tsId, {
    data: JSON.parse(JSON.stringify(result)),
    timestamp: Date.now()
  });

  return result;
}

export async function saveTimesheet(employeeId, month, timesheetData) {
  const tsId = `timesheet_${employeeId}_${month}`;
  const tsRef = doc(db, 'timesheets', tsId);
  await setDoc(tsRef, timesheetData, { merge: true });
  // Invalidate cache immediately so subsequent reads fetch fresh data
  timesheetCache.delete(tsId);
}

export async function cleanGarbageTimesheets() {
  const q = query(collection(db, 'timesheets'));
  const snap = await getDocs(q);
  let count = 0;
  
  const now = new Date();
  const currentMonthStr = now.toISOString().slice(0, 7);

  const deletePromises = [];

  snap.forEach(document => {
    const data = document.data();
    if (data.month && data.month < currentMonthStr) {
      if ((!data.summary || data.summary.netSalary === 0) && !data.transferSlipUrl) {
        let hasEntries = false;
        if (data.days) {
          for (let d = 1; d <= 31; d++) {
            const day = data.days[d];
            if (day && (day.timeIn || day.timeOut || day.advance > 0 || (day.overrideAmount !== undefined && day.overrideAmount !== null && day.overrideAmount !== '') || (day.lateMinutes && parseInt(day.lateMinutes) > 0))) {
              hasEntries = true;
              break;
            }
          }
        }
        if (!hasEntries) {
          deletePromises.push(deleteDoc(doc(db, 'timesheets', document.id)));
          count++;
        }
      }
    }
  });

  await Promise.all(deletePromises);
  clearTimesheetCache();
  return count;
}

// ==========================================================================
// CALENDAR EVENTS CRUD
// ==========================================================================
export async function getCalendar(companyId, year) {
  const calId = `calendar_${companyId}_${year}`;
  const calRef = doc(db, 'calendars', calId);
  const snap = await getDoc(calRef);
  if (snap.exists()) {
    return snap.data();
  } else {
    const defaults = {
      id: calId,
      companyId,
      year: parseInt(year),
      holidays: [],
      specialWorkdays: []
    };
    await setDoc(calRef, defaults);
    return defaults;
  }
}

export async function saveCalendar(companyId, year, calendarData) {
  const calId = `calendar_${companyId}_${year}`;
  const calRef = doc(db, 'calendars', calId);
  await setDoc(calRef, calendarData, { merge: true });
}

// ==========================================================================
// PUBLIC HOLIDAYS CACHE CRUD
// ==========================================================================
export async function getPublicHolidaysCache(year) {
  const cacheRef = doc(db, 'public_holidays', `year_${year}`);
  const snap = await getDoc(cacheRef);
  if (snap.exists()) {
    return snap.data();
  }
  return null;
}

export async function savePublicHolidaysCache(year, holidays) {
  const cacheRef = doc(db, 'public_holidays', `year_${year}`);
  await setDoc(cacheRef, {
    year: parseInt(year),
    holidays: holidays,
    lastUpdated: Date.now()
  }, { merge: true });
}

export async function cleanUpOldHolidaysCache(currentYear) {
  try {
    const q = query(collection(db, 'public_holidays'));
    const snap = await getDocs(q);
    const minYear = parseInt(currentYear) - 2;
    const maxYear = parseInt(currentYear) + 2;
    
    snap.forEach(async (document) => {
      const data = document.data();
      if (data.year < minYear || data.year > maxYear) {
        await deleteDoc(doc(db, 'public_holidays', document.id));
      }
    });
  } catch (err) {
    console.error('Error cleaning up old holidays cache:', err);
  }
}

// ==========================================================================
// FILE STORAGE UPLOADS & GOOGLE DRIVE INTEGRATION
// ==========================================================================
export async function getStorageUsage() {
  const metaRef = doc(db, 'system', 'storage');
  const snap = await getDoc(metaRef);
  if (snap.exists()) {
    return snap.data().totalBytesUsed || 0;
  }
  return 0;
}

export async function updateStorageUsage(bytesDelta) {
  const metaRef = doc(db, 'system', 'storage');
  const snap = await getDoc(metaRef);
  if (!snap.exists()) {
    await setDoc(metaRef, { totalBytesUsed: Math.max(0, bytesDelta) });
  } else {
    await updateDoc(metaRef, {
      totalBytesUsed: increment(bytesDelta)
    });
  }
}

export async function uploadToGoogleDriveViaScript(scriptUrl, file) {
  const urlToUse = scriptUrl || 'https://script.google.com/macros/s/AKfycbzbMpQ7_dM6OY7QzxHaTHrem3ABx4K0uDIUGKM_A8qR9P3pvIxdJNF8Bl7rujQpQDJcZQ/exec';
  
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async function(e) {
      try {
        const base64Data = e.target.result.split(',')[1];
        const payload = {
          fileName: file.name,
          mimeType: file.type,
          base64Data: base64Data
        };
        
        const response = await fetch(urlToUse, {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'text/plain;charset=utf-8'
          },
          body: JSON.stringify(payload)
        });
        
        const result = await response.json();
        if (result.success) {
          resolve(result.url);
        } else {
          reject(new Error(result.error || "เกิดข้อผิดพลาดในการบันทึกไฟล์ลง Google Drive"));
        }
      } catch(err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error("Failed to read file for Google Drive upload"));
    reader.readAsDataURL(file);
  });
}

export async function deleteEmployeeAttachment(employeeId, attachIndex) {
  const empRef = doc(db, 'employees', employeeId);
  const empSnap = await getDoc(empRef);
  if (empSnap.exists()) {
    const data = empSnap.data();
    const attachments = data.attachments || [];
    if (attachIndex >= 0 && attachIndex < attachments.length) {
      const removed = attachments.splice(attachIndex, 1)[0];
      // Decrement storage usage if it was on Firebase
      if (removed.size && removed.storageType !== 'google_drive') {
        await updateStorageUsage(-removed.size);
      }
      await updateDoc(empRef, { attachments });
    }
  }
}

async function withTimeout(promise, ms, errorMessage) {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error(errorMessage));
    }, ms);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => clearTimeout(timeoutId));
}

async function fetchViaScriptProxy(paramName, paramValue, scriptUrl) {
  const fetchUrl = `${scriptUrl}?${paramName}=${encodeURIComponent(paramValue)}`;
  const response = await fetch(fetchUrl);
  const json = await response.json();
  if (json.success) {
    const byteCharacters = atob(json.base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: json.mimeType });
  } else {
    throw new Error(json.error || "สคริปต์รายงานข้อผิดพลาดในการโหลดไฟล์");
  }
}

export async function getAttachmentBlob(url, scriptUrl) {
  // 1. If it's a Firebase Storage URL, try SDK getBlob first
  if (url.includes('firebasestorage.googleapis.com')) {
    const parts = url.split('/o/');
    if (parts.length > 1) {
      const pathPart = parts[1].split('?')[0];
      const storagePath = decodeURIComponent(pathPart);
      const fileRef = ref(storage, storagePath);
      try {
        // Try GCS getBlob with 4 seconds timeout
        return await withTimeout(
          getBlob(fileRef),
          4000,
          "ดาวน์โหลดปกติหมดเวลา"
        );
      } catch (err) {
        console.warn("Firebase Storage getBlob failed or timed out. Falling back to Apps Script proxy...", err);
        if (scriptUrl) {
          // Fallback to Apps Script proxy
          return await withTimeout(
            fetchViaScriptProxy('url', url, scriptUrl),
            8000,
            "การดาวน์โหลดไฟล์ผ่านตัวแทน Google Apps Script หมดเวลา (กรุณาตรวจสอบว่าได้ทำการอัปเดตโค้ดและ Deploy เวอร์ชันใหม่แล้ว)"
          );
        }
        throw new Error("ดาวน์โหลดไฟล์ไม่สำเร็จเนื่องจากติดสิทธิ์ความปลอดภัย CORS และไม่ได้ตั้งค่า Google Apps Script สำรองไว้");
      }
    }
  }
  
  // 2. If it's a Google Drive URL, use Apps Script fileId fetch (highly reliable for Drive)
  if (url.includes('drive.google.com') && scriptUrl) {
    let fileId = '';
    const dParts = url.split('/d/');
    if (dParts.length > 1) {
      fileId = dParts[1].split('/')[0];
    } else {
      const qParts = url.split('id=');
      if (qParts.length > 1) {
        fileId = qParts[1].split('&')[0];
      }
    }
    
    if (fileId) {
      return await withTimeout(
        fetchViaScriptProxy('fileId', fileId, scriptUrl),
        8000,
        "การดาวน์โหลดไฟล์จาก Google Drive หมดเวลา (กรุณาตรวจสอบว่าได้ทำการอัปเดตโค้ดและ Deploy เวอร์ชันใหม่แล้ว)"
      );
    }
  }
  
  // 3. Fallback: direct fetch
  try {
    return await withTimeout(
      fetch(url).then(r => r.blob()),
      5000,
      "ดาวน์โหลดไฟล์ตรงหมดเวลา"
    );
  } catch (err) {
    if (scriptUrl) {
      // Final fallback to Apps Script proxy
      return await withTimeout(
        fetchViaScriptProxy('url', url, scriptUrl),
        8000,
        "ดาวน์โหลดตัวแทนหมดเวลา"
      );
    }
    throw err;
  }
}

export async function uploadTransferSlip(employeeId, month, file) {
  const webpFile = await convertToWebP(file);
  const fileSize = webpFile.size;
  
  // Fetch employee to get company settings and script URL
  const empRef = doc(db, 'employees', employeeId);
  const empSnap = await getDoc(empRef);
  if (!empSnap.exists()) throw new Error("Employee not found");
  const empData = empSnap.data();
  const settings = await getCompanySettings(empData.companyId);
  
  // Check storage limit
  const currentUsage = await getStorageUsage();
  const limit = 4.8 * 1024 * 1024 * 1024; // 4.8 GB
  
  let url = '';
  let isGoogleDrive = false;
  
  if (currentUsage + fileSize > limit) {
    // Upload to Google Drive via Apps Script
    url = await uploadToGoogleDriveViaScript(settings.googleAppsScriptUrl, webpFile);
    isGoogleDrive = true;
  } else {
    // Upload to Firebase Storage
    const storagePath = `slips/${employeeId}_${month}.webp`;
    const fileRef = ref(storage, storagePath);
    await uploadBytes(fileRef, webpFile);
    url = await getDownloadURL(fileRef);
    
    // Increment storage usage
    await updateStorageUsage(fileSize);
  }
  
  // Save reference to timesheet
  const tsId = `timesheet_${employeeId}_${month}`;
  const tsRef = doc(db, 'timesheets', tsId);
  
  // Get existing timesheet to check if there was a previous slip
  const tsSnap = await getDoc(tsRef);
  if (tsSnap.exists()) {
    const tsData = tsSnap.data();
    if (tsData.transferSlipSize && tsData.transferSlipStorageType !== 'google_drive') {
      // Subtract old slip size from Firebase storage usage
      await updateStorageUsage(-tsData.transferSlipSize);
    }
  }
  
  await updateDoc(tsRef, {
    transferSlipUrl: url,
    transferSlipSize: fileSize,
    transferSlipStorageType: isGoogleDrive ? 'google_drive' : 'firebase_storage',
    transferSlipUploadedAt: new Date().toISOString().split('T')[0]
  });
  
  return url;
}

export async function uploadEmployeeDocument(employeeId, docName, file) {
  const processedFile = await convertToWebP(file);
  const fileSize = processedFile.size;
  
  // Fetch employee to get company settings and script URL
  const empRef = doc(db, 'employees', employeeId);
  const empSnap = await getDoc(empRef);
  if (!empSnap.exists()) throw new Error("Employee not found");
  const empData = empSnap.data();
  const settings = await getCompanySettings(empData.companyId);
  
  // Check storage limit
  const currentUsage = await getStorageUsage();
  const limit = 4.8 * 1024 * 1024 * 1024; // 4.8 GB
  
  let url = '';
  let isGoogleDrive = false;
  
  if (currentUsage + fileSize > limit) {
    // Upload to Google Drive via Apps Script
    url = await uploadToGoogleDriveViaScript(settings.googleAppsScriptUrl, processedFile);
    isGoogleDrive = true;
  } else {
    // Upload to Firebase Storage
    const ext = processedFile.name.split('.').pop();
    const timestamp = Date.now();
    const storagePath = `documents/${employeeId}/${timestamp}_${docName}.${ext}`;
    const fileRef = ref(storage, storagePath);
    
    await uploadBytes(fileRef, processedFile);
    url = await getDownloadURL(fileRef);
    
    // Increment storage usage
    await updateStorageUsage(fileSize);
  }
  
  // Append attachment to employee profile
  const attachments = empData.attachments || [];
  attachments.push({
    name: docName,
    url,
    size: fileSize,
    storageType: isGoogleDrive ? 'google_drive' : 'firebase_storage',
    uploadedAt: new Date().toISOString().split('T')[0]
  });
  
  await updateDoc(empRef, { attachments });
  
  return url;
}

// ==========================================================================
// DATABASE PRE-SEEDING (Mock data initialization)
// ==========================================================================
export async function initializeDefaultDataIfNeeded() {
  // Check if Master Admin already exists
  const masterRef = doc(db, 'employees', 'emp_master');
  const snap = await getDoc(masterRef);
  
  if (!snap.exists()) {
    console.log("Initializing database with Master Admin and mock employees...");
    
    // Seed Company 1
    const itemboxSettings = {
      id: 'itembox',
      name: 'ITEMBOX',
      address: '123/45 อาคารไอเทมบ็อกซ์ ชั้น 5 ถนนรัชดาภิเษก แขวงดินแดง เขตดินแดง กรุงเทพมหานคร 10400',
      taxId: '0105566000123',
      phone: '02-123-4567',
      theme: 'peach-cream',
      socialSecurityRate: 5,
      socialSecurityMin: 82.5,
      socialSecurityMax: 875,
      withholdingTaxRate: 3,
      authorizedPayors: ['นัททราย ดุสิตตานครินทร์'],
      enableSickLeaveCalc: true,
      enableAnnualLeaveCalc: true,
      holidaySystem: 'admin_defined',
      diligenceAllowanceAmount: 1000,
      diligenceRequireNoLate: true,
      diligenceRequireNoAbsence: true,
      diligenceRequireNoSickLeave: true,
      diligenceRequireNoAnnualLeave: true,
      diligenceRequireNoPersonalLeave: true,
      workHoursPerDay: {
        monday: 8, tuesday: 8, wednesday: 8, thursday: 8, friday: 8, saturday: 8, sunday: 0
      }
    };
    await setDoc(doc(db, 'companies', 'itembox'), itemboxSettings);

    // Seed Company 2
    const secondCompanySettings = {
      id: 'second_co',
      name: 'MEANBOOK',
      address: '88/9 อาคารสเปซกริด ชั้น 12 ถนนสุขุมวิท แขวงคลองเตย เขตคลองเตย กรุงเทพมหานคร 10110',
      taxId: '0105567000987',
      phone: '02-987-6543',
      theme: 'cool-grey',
      socialSecurityRate: 5,
      socialSecurityMin: 82.5,
      socialSecurityMax: 875,
      withholdingTaxRate: 3,
      authorizedPayors: ['นัททราย ดุสิตตานครินทร์'],
      enableSickLeaveCalc: false, // Sick leave computation turned off
      enableAnnualLeaveCalc: true,
      holidaySystem: 'employee_selected',
      diligenceAllowanceAmount: 1000,
      diligenceRequireNoLate: true,
      diligenceRequireNoAbsence: true,
      diligenceRequireNoSickLeave: true,
      diligenceRequireNoAnnualLeave: true,
      diligenceRequireNoPersonalLeave: true,
      workHoursPerDay: {
        monday: 8, tuesday: 8, wednesday: 8, thursday: 8, friday: 8, saturday: 0, sunday: 0 // Saturday is day off
      }
    };
    await setDoc(doc(db, 'companies', 'second_co'), secondCompanySettings);

    // Seed Master User
    const masterAdmin = {
      id: 'emp_master',
      companyId: 'itembox',
      realName: 'นัททราย ดุสิตตานครินทร์',
      nickname: 'เม',
      citizenId: '1234567890123',
      bankAccount: '111-2-22222-3',
      startDate: '2022-01-01',
      socialSecurityStartDate: '2022-01-01',
      status: 'active',
      resignedDate: null,
      type: 'salary',
      salaryRate: 50000,
      weeklyDaysOff: [0], // Sunday
      isIntern: false,
      requiresSecurityDeposit: false,
      securityDepositAmount: 0,
      securityDepositStatus: 'none',
      studentLoanFund: {
        enabled: false,
        monthlyAmount: 0
      },
      recurringItems: [
        { name: 'ค่าเดินทาง', type: 'income', amount: 3000 }
      ],
      email: 'maysaywuru@gmail.com',
      username: 'maywuru',
      role: 'admin', // Full administrative access across both companies
      attachments: []
    };
    await setDoc(masterRef, masterAdmin);

    // Seed 4 Mock Employees for ItemBox (Company 1)
    const mockEmployeesItembox = [
      {
        id: 'emp_ib_001',
        companyId: 'itembox',
        realName: 'สมศักดิ์ แสนสุข',
        nickname: 'สม',
        citizenId: '3210987654321',
        bankAccount: '555-6-77777-8',
        startDate: '2024-02-15',
        socialSecurityStartDate: '2024-03-01',
        status: 'active',
        resignedDate: null,
        type: 'salary',
        salaryRate: 18000,
        weeklyDaysOff: [0],
        isIntern: false,
        requiresSecurityDeposit: true,
        securityDepositAmount: 3000,
        securityDepositStatus: 'deducted',
        studentLoanFund: { enabled: true, monthlyAmount: 1000 },
        recurringItems: [{ name: 'เบี้ยเลี้ยงพิเศษ', type: 'income', amount: 500 }],
        email: 'somsak@gmail.com',
        username: 'somsak',
        role: 'employee',
        attachments: []
      },
      {
        id: 'emp_ib_002',
        companyId: 'itembox',
        realName: 'อรทัย โชติช่วง',
        nickname: 'อร',
        citizenId: '4560123456789',
        bankAccount: '666-7-88888-9',
        startDate: '2025-01-10',
        socialSecurityStartDate: '2025-01-10',
        status: 'active',
        resignedDate: null,
        type: 'daily', // Daily worker
        salaryRate: 600, // 600 Baht per day
        weeklyDaysOff: [0],
        isIntern: false,
        requiresSecurityDeposit: false,
        securityDepositAmount: 0,
        securityDepositStatus: 'none',
        studentLoanFund: { enabled: false, monthlyAmount: 0 },
        recurringItems: [],
        email: 'oratai@gmail.com',
        username: 'oratai',
        role: 'employee',
        attachments: []
      },
      {
        id: 'emp_ib_003',
        companyId: 'itembox',
        realName: 'วิโรจน์ รักเรียน',
        nickname: 'บอย',
        citizenId: '1212121212121',
        bankAccount: '222-3-44444-5',
        startDate: '2026-05-01',
        socialSecurityStartDate: null,
        status: 'active',
        resignedDate: null,
        type: 'salary',
        salaryRate: 12000,
        weeklyDaysOff: [0],
        isIntern: true, // Intern: withholding tax only, no social security
        requiresSecurityDeposit: false,
        securityDepositAmount: 0,
        securityDepositStatus: 'none',
        studentLoanFund: { enabled: false, monthlyAmount: 0 },
        recurringItems: [],
        email: 'boyintern@gmail.com',
        username: 'boyintern',
        role: 'employee',
        attachments: []
      },
      {
        id: 'emp_ib_004',
        companyId: 'itembox',
        realName: 'เกศรา คำอินทร์',
        nickname: 'ฝน',
        citizenId: '9876543210987',
        bankAccount: '999-8-77777-6',
        startDate: '2023-01-01',
        socialSecurityStartDate: '2023-01-01',
        status: 'resigned', // Resigned employee for archive check
        resignedDate: '2024-03-15', // Resigned > 2 years ago relative to May 2026
        type: 'salary',
        salaryRate: 20000,
        weeklyDaysOff: [0],
        isIntern: false,
        requiresSecurityDeposit: true,
        securityDepositAmount: 5000,
        securityDepositStatus: 'refunded', // Refunded upon resignation
        studentLoanFund: { enabled: false, monthlyAmount: 0 },
        recurringItems: [],
        email: 'fonresigned@gmail.com',
        username: 'fonresigned',
        role: 'employee',
        attachments: []
      }
    ];

    for (const emp of mockEmployeesItembox) {
      await setDoc(doc(db, 'employees', emp.id), emp);
    }

    // Seed 4 Mock Employees for SpaceGrid (Company 2)
    const mockEmployeesSpacegrid = [
      {
        id: 'emp_sg_001',
        companyId: 'second_co',
        realName: 'ธนา กิจรักษา',
        nickname: 'ท็อป',
        citizenId: '1122334455667',
        bankAccount: '777-8-99999-0',
        startDate: '2023-08-01',
        socialSecurityStartDate: '2023-08-01',
        status: 'active',
        resignedDate: null,
        type: 'salary',
        salaryRate: 35000,
        weeklyDaysOff: [0, 6], // Saturday and Sunday off
        isIntern: false,
        requiresSecurityDeposit: false,
        securityDepositAmount: 0,
        securityDepositStatus: 'none',
        studentLoanFund: { enabled: true, monthlyAmount: 1500 },
        recurringItems: [{ name: 'ค่าอินเทอร์เน็ต', type: 'income', amount: 800 }],
        email: 'thana@gmail.com',
        username: 'thana',
        role: 'manager_a', // Manager with full access to company B only
        attachments: []
      },
      {
        id: 'emp_sg_002',
        companyId: 'second_co',
        realName: 'กวี อักษรไทย',
        nickname: 'กานต์',
        citizenId: '2233445566778',
        bankAccount: '', // No bank account (cash payment)
        startDate: '2025-06-01',
        socialSecurityStartDate: null,
        status: 'active',
        resignedDate: null,
        type: 'freelance', // Freelance
        salaryRate: 15000, // Fixed freelance budget per contract/month
        weeklyDaysOff: [], // Freelancer has no weekly days off limit
        isIntern: false,
        requiresSecurityDeposit: false,
        securityDepositAmount: 0,
        securityDepositStatus: 'none',
        studentLoanFund: { enabled: false, monthlyAmount: 0 },
        recurringItems: [],
        email: 'kawee@gmail.com',
        username: 'kawee',
        role: 'employee',
        attachments: []
      },
      {
        id: 'emp_sg_003',
        companyId: 'second_co',
        realName: 'สุดาพร งดงาม',
        nickname: 'หมิว',
        citizenId: '3344556677889',
        bankAccount: '888-9-00000-1',
        startDate: '2024-11-01',
        socialSecurityStartDate: '2024-11-01',
        status: 'active',
        resignedDate: null,
        type: 'salary',
        salaryRate: 22000,
        weeklyDaysOff: [0], // Only Sunday off
        isIntern: false,
        requiresSecurityDeposit: true,
        securityDepositAmount: 4000,
        securityDepositStatus: 'deducted',
        studentLoanFund: { enabled: false, monthlyAmount: 0 },
        recurringItems: [],
        email: 'mew@gmail.com',
        username: 'mew',
        role: 'employee',
        attachments: []
      },
      {
        id: 'emp_sg_004',
        companyId: 'second_co',
        realName: 'นริศรา ผิวดี',
        nickname: 'จอย',
        citizenId: '4455667788990',
        bankAccount: '333-4-55555-6',
        startDate: '2025-12-01',
        socialSecurityStartDate: '2025-12-01',
        status: 'resigned', // Resigned employee, but only 5 months ago
        resignedDate: '2026-01-15', // Resigned < 2 years ago, will NOT be archived yet
        type: 'salary',
        salaryRate: 25000,
        weeklyDaysOff: [0],
        isIntern: false,
        requiresSecurityDeposit: true,
        securityDepositAmount: 5000,
        securityDepositStatus: 'refunded',
        studentLoanFund: { enabled: false, monthlyAmount: 0 },
        recurringItems: [],
        email: 'joy@gmail.com',
        username: 'joy',
        role: 'employee',
        attachments: []
      }
    ];

    for (const emp of mockEmployeesSpacegrid) {
      await setDoc(doc(db, 'employees', emp.id), emp);
    }
    
    // Seed standard calendars for 2026
    const itemboxCalendar = {
      id: 'calendar_itembox_2026',
      companyId: 'itembox',
      year: 2026,
      holidays: [
        { date: '2026-01-01', name: 'วันขึ้นปีใหม่' },
        { date: '2026-04-13', name: 'วันมหาสงกรานต์' },
        { date: '2026-04-14', name: 'วันสงกรานต์' },
        { date: '2026-05-01', name: 'วันแรงงานแห่งชาติ' },
        { date: '2026-12-05', name: 'วันพ่อแห่งชาติ' }
      ],
      specialWorkdays: [
        { date: '2026-05-02', name: 'ทำงานพิเศษวันแรงงานทดแทน', compensatoryOffDate: '2026-05-18', isCompensated: true }
      ]
    };
    await setDoc(doc(db, 'calendars', 'calendar_itembox_2026'), itemboxCalendar);
    
    const secondCoCalendar = {
      id: 'calendar_second_co_2026',
      companyId: 'second_co',
      year: 2026,
      holidays: [
        { date: '2026-01-01', name: 'วันขึ้นปีใหม่' },
        { date: '2026-05-01', name: 'วันแรงงานแห่งชาติ' }
      ],
      specialWorkdays: [] // No special workdays yet
    };
    await setDoc(doc(db, 'calendars', 'calendar_second_co_2026'), secondCoCalendar);

    console.log("Pre-seeding completed successfully!");
  } else {
    console.log("Database already initialized.");
    const data = snap.data();
    
    let needsMigration = !data.email || data.nickname === 'นัท' ;
    const itemboxSettingsRef = doc(db, 'companies', 'itembox');
    const ibSnap = await getDoc(itemboxSettingsRef);
    if (ibSnap.exists() && ibSnap.data().diligenceAllowanceAmount === undefined) {
      needsMigration = true;
    }

    if (needsMigration) {
      console.log("Migrating database documents for Khun May (nickname, email, and diligence updates)...");
      try {
        await updateDoc(masterRef, {
          email: 'maysaywuru@gmail.com',
          nickname: 'เม',
          realName: 'นัททราย ดุสิตตานครินทร์'
        });

        // Update company Settings with new spelling and diligence defaults
        if (ibSnap.exists()) {
          const ibData = ibSnap.data();
          const updates = {
            authorizedPayors: ['นัททราย ดุสิตตานครินทร์']
          };
          if (ibData.diligenceAllowanceAmount === undefined) {
            updates.diligenceAllowanceAmount = 1000;
            updates.diligenceRequireNoLate = true;
            updates.diligenceRequireNoAbsence = true;
            updates.diligenceRequireNoSickLeave = true;
            updates.diligenceRequireNoAnnualLeave = true;
            updates.diligenceRequireNoPersonalLeave = true;
          }
          await updateDoc(itemboxSettingsRef, updates);
        }
        
        const secondSettingsRef = doc(db, 'companies', 'second_co');
        const scSnap = await getDoc(secondSettingsRef);
        if (scSnap.exists()) {
          const scData = scSnap.data();
          const updates = {
            authorizedPayors: ['นัททราย ดุสิตตานครินทร์']
          };
          if (scData.diligenceAllowanceAmount === undefined) {
            updates.diligenceAllowanceAmount = 1000;
            updates.diligenceRequireNoLate = true;
            updates.diligenceRequireNoAbsence = true;
            updates.diligenceRequireNoSickLeave = true;
            updates.diligenceRequireNoAnnualLeave = true;
            updates.diligenceRequireNoPersonalLeave = true;
          }
          await updateDoc(secondSettingsRef, updates);
        }

        const mockEmails = {
          'emp_ib_001': 'somsak@gmail.com',
          'emp_ib_002': 'oratai@gmail.com',
          'emp_ib_003': 'boyintern@gmail.com',
          'emp_ib_004': 'fonresigned@gmail.com',
          'emp_sg_001': 'thana@gmail.com',
          'emp_sg_002': 'kawee@gmail.com',
          'emp_sg_003': 'mew@gmail.com',
          'emp_sg_004': 'joy@gmail.com'
        };
        
        for (const [id, email] of Object.entries(mockEmails)) {
          const ref = doc(db, 'employees', id);
          const s = await getDoc(ref);
          if (s.exists()) {
            await updateDoc(ref, { email });
          }
        }
        console.log("Migration completed successfully.");
      } catch (err) {
        console.error("Migration error:", err);
      }
    }
  }
}
